import React, { useState } from 'react';
import cx from 'classnames';
import { string } from 'yup';

import { GdprFields, MarketoForm } from 'src/components/Forms';
import { PhoneField } from 'src/components/Forms/PhoneField';
import Btn from 'src/components-v2/Inputs/Button/Btn';
import {
  Button,
  SelectWithController,
  TextArea,
  TextInput,
} from 'src/components/Inputs';
import EmployeeCount from 'src/components/Inputs/EmployeeCount/EmployeeCount';
import { FormSectionCovidWaiver } from './FormSectionCovidWaiver';
import { FormSectionDisclaimer } from './FormSectionDisclaimer';
import { FormSectionHopInCode } from './FormSectionHopInCode';
import { FormSectionNameFields } from './FormSectionNameFields';
import { FormSectionPxFields } from './FormSectionPxFields';
import { FormSectionRichDescription } from './FormSectionRichDescription';
import { FormSectionSolutionsFields } from './FormSectionSolutionsFields';
import { FormSectionTextInput } from './FormSectionTextInput';
import { FormSectionTicketType } from './FormSectionTicketType';
import { FormSectionWaitList } from './FormSectionWaitList';

// helpers
import { usePageLocale } from 'src/contexts';
import { useCmsTheme } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';
import { getRequiredMessage, mutateHardCodedLinks } from 'src/lib/utils';

import { CmsThemes, Locales } from 'src/lib/constants';
import { Props } from './FormSection.types';
import styles from './form-section-web3.module.scss';

import * as enEuForm from 'src/store/en-eu/form';
import * as enGbForm from 'src/store/en-gb/form';
import * as enUsForm from 'src/store/form';

const formPerLocale = {
  [Locales.EN_US]: enUsForm,
  [Locales.EN_EU]: enEuForm,
  [Locales.EN_GB]: enGbForm,
};

const FormSection: React.FC<Props> = ({
  handleFormSubmit,
  setIsFormSubmitted,
  showDescription = true,
  usePageTheme = false,
  ...props
}) => {
  const cmsTheme = useCmsTheme();
  const isWeb3 = usePageTheme && cmsTheme === CmsThemes.WEB3;
  const inputGroupClassName = cx(styles.inputGroup_web3);
  const selectGroupClassName = cx(styles.selectGroup_web3);
  const placeholderFor = (label?: string) => {
    return isWeb3 ? label : undefined;
  };
  const textFieldProps = isWeb3
    ? {
        className: inputGroupClassName,
        labelClassName: styles.label,
        inputClassName: styles.input,
        isDarkMode: false,
      }
    : {};
  const [state, setState] = useState({ ticketType: '', useCase: '' });

  const handleTicketTypeChange = ({ value }) => {
    setState((state) => {
      return { ...state, ticketType: value };
    });
  };

  const handleUseCaseOtherChange = ({ value }) => {
    setState((state) => {
      return { ...state, useCase: value };
    });
  };

  const locale = usePageLocale();
  const { formData } = mutateHardCodedLinks({
    data: formPerLocale[locale] || formPerLocale[Locales.EN_US],
    locale,
  });

  return (
    <>
      <FormSectionRichDescription
        body={props.formData.body}
        showDescription={showDescription}
      />
      <MarketoForm
        formType={props?.formType}
        redirectUrl={
          // we try the redirect url before falling back to marketo cms button href
          (props?.redirectOnSubmit && props?.redirectUrl) ||
          props.formData.marketoFormBtnUrl
        }
        marketoKey={props.formData['marketo-key']}
        locale={locale}
        onSubmit={() => {
          // TODO: [redacted]#############################
          // Decouple setIsFormSubmitted from Gated Content
          setIsFormSubmitted && setIsFormSubmitted(true);
          handleFormSubmit && handleFormSubmit();
        }}
        shouldForward={props.formData.shouldForward}
        trackingId={props.formData.trackingId}
        rememberEmail={props.formData?.rememberEmail}
        render={({
          control,
          register,
          errors,
          setValue,
          isSubmitting,
          clearErrors,
          getValues,
        }) => {
          return (
            <>
              {props.formData.includeNames !== false && (
                <FormSectionNameFields
                  formData={formData}
                  register={register}
                  errors={errors}
                  className={isWeb3 ? styles.namesRow_web3 : undefined}
                  fieldClassName={isWeb3 ? inputGroupClassName : undefined}
                  labelClassName={isWeb3 ? styles.label : undefined}
                  inputClassName={isWeb3 ? styles.input : undefined}
                  usePlaceholderLabels={isWeb3}
                />
              )}

              {props.formData?.includeCompany && (
                <FormSectionTextInput
                  data={formData.company}
                  register={register}
                  errors={errors}
                  required
                  placeholder={placeholderFor(formData.company.label)}
                  {...textFieldProps}
                />
              )}

              <FormSectionTextInput
                data={formData.email}
                register={register}
                errors={errors}
                required
                placeholder={placeholderFor(formData.email.label)}
                {...textFieldProps}
                defaultValue={getValues(formData.email.name)}
                pattern={{
                  value: string().email().tests[0].OPTIONS.params.regex,
                  message: formData.email.invalidMessage,
                }}
              />

              <EmployeeCount
                className={isWeb3 ? selectGroupClassName : undefined}
                controlClassName={isWeb3 ? styles.selectControl : undefined}
                labelClassName={isWeb3 ? styles.selectLabel : undefined}
                usePlaceholderLabels={isWeb3}
                control={control}
                errors={errors}
                locale={locale}
                marketoKey={props.formData['marketo-key']}
                register={register}
                setValue={setValue}
              />

              {props.formData?.includePhone && (
                <PhoneField
                  data={formData.phone}
                  register={register}
                  errors={errors}
                  className={isWeb3 ? inputGroupClassName : undefined}
                  labelClassName={isWeb3 ? styles.label : undefined}
                  inputClassName={isWeb3 ? styles.input : undefined}
                  isDarkMode={isWeb3 ? false : undefined}
                  getPlaceholder={placeholderFor}
                />
              )}

              {props.formData?.hopinIntegrationCode && (
                <FormSectionHopInCode
                  ticketType={state.ticketType}
                  register={register}
                  formData={props.formData}
                  setValue={setValue}
                />
              )}

              {props.formData?.includePxFieldset && (
                <FormSectionPxFields
                  control={control}
                  formData={formData}
                  errors={errors}
                  setValue={setValue}
                  register={register}
                />
              )}

              {props.formData?.includeJobTitle && (
                <FormSectionTextInput
                  data={formData.jobTitle}
                  register={register}
                  errors={errors}
                  placeholder={placeholderFor(formData.jobTitle.label)}
                  {...textFieldProps}
                />
              )}

              {props.formData?.includeSolutionsField && (
                <FormSectionSolutionsFields
                  formData={formData}
                  setValue={setValue}
                  errors={errors}
                  register={register}
                  clearErrors={clearErrors}
                  getValues={getValues}
                />
              )}

              {props.formData?.includeCommentsField && (
                <TextArea
                  {...formData.comments}
                  className={isWeb3 ? styles.textAreaGroup_web3 : undefined}
                  isLabelVisible={!isWeb3}
                  placeholder={
                    props.formData?.commentsFieldPlaceholder ||
                    (isWeb3
                      ? formData.comments.label
                      : formData.comments.placeholder)
                  }
                  errorMessage={errors?.[formData.comments.name]?.message}
                  required={getRequiredMessage(formData.comments.label)}
                  register={register}
                />
              )}

              <GdprFields
                className={isWeb3 ? selectGroupClassName : undefined}
                controlClassName={isWeb3 ? styles.selectControl : undefined}
                labelClassName={isWeb3 ? styles.selectLabel : undefined}
                usePlaceholderLabels={isWeb3}
                isDarkMode={isWeb3 ? false : undefined}
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                excludeActiveCountries={props.formData?.excludeActiveCountries}
                defaultCountryFromTimeZone={
                  props.formData?.defaultCountryFromTimeZone
                }
                includeState={props.formData?.includeState}
              />

              {props.formData?.includeTicketType && (
                <FormSectionTicketType
                  formData={formData}
                  register={register}
                  cmsProps={props}
                  errors={errors}
                  setValue={setValue}
                  control={control}
                  handleTicketTypeChange={handleTicketTypeChange}
                />
              )}

              {props.formData?.isWaitListSoldOut && (
                <FormSectionWaitList formData={formData} register={register} />
              )}

              {props.formData?.includeUseCase && (
                <SelectWithController
                  label={formData.plaidForumUseCase?.label}
                  name={formData.plaidForumUseCase?.name}
                  describedby={formData.plaidForumUseCase?.describedby}
                  isDarkMode={false}
                  id={formData.plaidForumUseCase?.name}
                  register={register}
                  required={getRequiredMessage(
                    formData.plaidForumUseCase?.label,
                  )}
                  errorMessage={
                    errors?.[formData.plaidForumUseCase?.name]?.message
                  }
                  setValue={setValue}
                  control={control}
                  options={formData.plaidForumUseCase.options}
                  onChange={handleUseCaseOtherChange}
                />
              )}

              {state.useCase === 'Other' && (
                <TextInput
                  label={formData.otherUseCaseExplained?.label}
                  id={formData.otherUseCaseExplained?.name}
                  name={formData.otherUseCaseExplained?.name}
                  describedby={formData.otherUseCaseExplained?.describedby}
                  required={getRequiredMessage(
                    formData.otherUseCaseExplained?.label,
                  )}
                  errorMessage={
                    errors?.[formData.otherUseCaseExplained?.name]?.message
                  }
                  register={register}
                />
              )}

              {props.formData?.includeTicketType &&
                state.ticketType === 'In person' && (
                  <FormSectionCovidWaiver
                    formData={formData}
                    register={register}
                  />
                )}

              {isWeb3 ? (
                <Btn
                  className={styles.submit}
                  type='submit'
                  variant='primary'
                  disabled={isSubmitting}
                  sx={{ width: '100%' }}
                >
                  {isSubmitting
                    ? 'Loading…'
                    : props.formData?.actionText || 'Contact Sales'}
                </Btn>
              ) : (
                <Button
                  className='button-cta mb1'
                  type='submit'
                  value={props.formData?.actionText || 'Contact Sales'}
                  fullWidth
                  showLoader={isSubmitting}
                  submitting={isSubmitting}
                >
                  {isSubmitting ? (
                    <>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Loading&hellip;</>
                  ) : (
                    props.formData?.actionText || 'Contact Sales'
                  )}
                </Button>
              )}

              <FormSectionDisclaimer
                formData={formData}
                cmsProps={props}
                className={isWeb3 ? styles.disclaimer_web3 : undefined}
              />
            </>
          );
        }}
      />
    </>
  );
};

export default FormSection;
